import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { getCompleteProfile, getChildcareProfile } from "../services/childcare";
import api from "../services/axiosInstance";

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [token, setToken] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [role, setRole] = useState(null);
  const [user, setUser] = useState(null);
  const [userType, setUserType] = useState(null);
  const [facility, setFacility] = useState(null);
  const [personalInfo, setPersonalInfo] = useState(null);
  const [facilityId, setFacilityId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [screenName, setScreenName] = useState(null);
  const [approvalPending, setApprovalPending] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const socketRef = useRef(null);

  // ✅ Define logout first using useCallback
  const logout = useCallback(() => {
    ["token", "adminToken", "partnerToken", "tutorToken", "user", "facilityId", "role"].forEach(
      (key) => localStorage.removeItem(key),
    );
    setToken(null);
    setIsLoggedIn(false);
    setCurrentUserId(null);
    setRole(null);
    setUser(null);
    setUserType(null);
    setFacility(null);
    setPersonalInfo(null);
    setFacilityId(null);
    setScreenName(null);
    setApprovalPending(false);
    setNotifications([]);
    setUnreadCount(0);
  }, []);

  // ✅ Define loadChildcareProfile with useCallback
  const loadChildcareProfile = useCallback(async () => {
    try {
      const response = await getCompleteProfile();
      if (response.data?.success) {
        const { facility: facilityData, personal_info } = response.data.data;
        setFacility(facilityData);
        setPersonalInfo(personal_info);
        if (facilityData?.id) {
          setFacilityId(facilityData.id);
          localStorage.setItem("facilityId", facilityData.id);
        }
      }
    } catch (error) {
      try {
        const basicResponse = await getChildcareProfile();
        if (basicResponse.data?.success) {
          const facilityData = basicResponse.data.data.facility;
          setFacility(facilityData);
          if (facilityData?.id) {
            setFacilityId(facilityData.id);
            localStorage.setItem("facilityId", facilityData.id);
          }
        }
      } catch (err) {
        console.error("Error loading facility:", err);
      }
    }
  }, []);
  const CHILDCARE_ROLES = [
    "childcare_admin",
    "childcare_co_admin",
    "childcare_caregiver",
    "childcare_tutor",
  ];

  // Connect socket and fetch notifications — only for childcare users.
  // Parents and other roles have their own notification systems.
  useEffect(() => {
    if (!isLoggedIn) {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      setNotifications([]);
      setUnreadCount(0);
      return;
    }

    const token = localStorage.getItem("token");
    if (!token) return;

    // Decode user type from JWT before making any role-specific API call
    let userRole = null;
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      userRole = payload.user_type || payload.role;
    } catch (_) {}

    if (!CHILDCARE_ROLES.includes(userRole)) {
      // Not a childcare user — skip childcare notifications and socket
      return;
    }

    // Fetch existing notifications
    api
      .get("/childcare/notifications", { params: { limit: 50 } })
      .then((res) => {
        if (res.data?.success) {
          setNotifications(res.data.data || []);
          setUnreadCount(res.data.unread_count || 0);
        }
      })
      .catch(() => {});

    // socket.io-client is loaded on demand — anonymous visitors (almost all
    // homepage traffic) never pull it into the main bundle.
    let cancelled = false;
    import("socket.io-client").then(({ io }) => {
      if (cancelled) return;

      const socket = io(process.env.REACT_APP_SOCKET_URL, {
        auth: { token },
        transports: ["websocket", "polling"],
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 2000,
      });

      socket.on("notification:new", (notif) => {
        setNotifications((prev) => [notif, ...prev]);
        setUnreadCount((prev) => prev + 1);
      });

      socket.on("connect_error", (error) => {
        if (error.message === "Invalid token") {
          socket.disconnect();
          console.warn("Socket auth rejected — session may be stale");
        }
      });

      socketRef.current = socket;
    });

    return () => {
      cancelled = true;
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [isLoggedIn]); // eslint-disable-line react-hooks/exhaustive-deps

  const markAllRead = useCallback(async () => {
    try {
      await api.put("/childcare/notifications/read-all");
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: 1 })));
      setUnreadCount(0);
    } catch (_) {}
  }, []);

  const markOneRead = useCallback(async (id) => {
    try {
      await api.put(`/childcare/notifications/${id}/read`);
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, is_read: 1 } : n))
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (_) {}
  }, []);


  // ✅ Fixed updateUser - updates both user, personalInfo, and facility
  const updateUser = useCallback((updatedData) => {
    setUser((prevUser) => {
      const newUser = { ...prevUser, ...updatedData };
      localStorage.setItem("user", JSON.stringify(newUser));
      return newUser;
    });

    // Update personalInfo if provided
    if (updatedData.personal_info) {
      setPersonalInfo(updatedData.personal_info);
    }

    // Update facility if provided
    if (updatedData.facility) {
      setFacility(updatedData.facility);
    }
  }, []);

  // ✅ Refresh user data from API
  const refreshUserData = useCallback(async () => {
    try {
      const response = await getCompleteProfile();
      if (response.data?.success && response.data?.data) {
        const { facility: facilityData, personal_info } = response.data.data;

        updateUser({
          facility: facilityData,
          personal_info: personal_info,
          facility_name: facilityData?.name,
          email: facilityData?.email || personal_info?.email,
          contact_number:
            facilityData?.contact_number || personal_info?.contact_number,
        });

        return true;
      }
      return false;
    } catch (error) {
      console.error("Error refreshing user data:", error);
      return false;
    }
  }, [updateUser]);

  // ✅ Fixed useEffect with proper dependencies
  useEffect(() => {
    const initializeAuth = async () => {
      const storedToken = localStorage.getItem("token");
      const storedUser = localStorage.getItem("user");

      if (storedToken) {
        setToken(storedToken);
        setIsLoggedIn(true);
        try {
          const payload = JSON.parse(atob(storedToken.split(".")[1]));
          setCurrentUserId(payload.id);
          setRole(payload.role || payload.user_type);
          setUserType(payload.user_type || payload.role);

          if (payload.facilityId) {
            setFacilityId(payload.facilityId);
            localStorage.setItem("facilityId", payload.facilityId);
          }

          if (storedUser) {
            const userData = JSON.parse(storedUser);
            setUser(userData);
            if (userData.screenName) setScreenName(userData.screenName);
            if (userData.approval_pending) setApprovalPending(true);
          } else {
            setUser({
              id: payload.id,
              email: payload.email,
              fname: payload.fname,
              user_type: payload.user_type,
              facilityId: payload.facilityId,
            });
          }

          const userTypeValue = payload.user_type || payload.role;
          if (
            userTypeValue === "childcare_admin" ||
            userTypeValue === "childcare_co_admin" ||
            userTypeValue === "childcare_caregiver" ||
            userTypeValue === "childcare_tutor"
          ) {
            await loadChildcareProfile();
          }
        } catch (err) {
          console.error("Invalid token", err);
          logout();
        }
      }
      setLoading(false);
    };

    initializeAuth();
  }, [logout, loadChildcareProfile]);

  const login = useCallback(
    (newToken, userData = null) => {
      localStorage.setItem("token", newToken);
      setToken(newToken);
      if (userData) {
        localStorage.setItem("user", JSON.stringify(userData));
        setUser(userData);
        if (userData.screenName) setScreenName(userData.screenName);
        setApprovalPending(!!userData.approval_pending);
      }
      setIsLoggedIn(true);

      try {
        const payload = JSON.parse(atob(newToken.split(".")[1]));
        setCurrentUserId(payload.id);
        setRole(payload.role || payload.user_type);
        setUserType(payload.user_type || payload.role);

        if (payload.facilityId || userData?.facilityId) {
          const fid = payload.facilityId || userData?.facilityId;
          setFacilityId(fid);
          localStorage.setItem("facilityId", fid);
        }

        const userTypeValue = payload.user_type || payload.role;
        if (
          userTypeValue === "childcare_admin" ||
          userTypeValue === "childcare_co_admin" ||
          userTypeValue === "childcare_caregiver" ||
          userTypeValue === "childcare_tutor"
        ) {
          setTimeout(() => loadChildcareProfile(), 100);
        }
      } catch (err) {
        console.error("Invalid token", err);
      }
    },
    [loadChildcareProfile],
  );

  const clearApprovalPending = useCallback(() => {
    setApprovalPending(false);
    setUser((prev) => {
      if (!prev) return prev;
      const updated = { ...prev, approval_pending: false };
      localStorage.setItem("user", JSON.stringify(updated));
      return updated;
    });
  }, []);

  const updateScreenName = useCallback(
    (newScreenName) => {
      setScreenName(newScreenName);
      const updatedUser = { ...user, screenName: newScreenName };
      setUser(updatedUser);
      localStorage.setItem("user", JSON.stringify(updatedUser));
    },
    [user],
  );

  const updateFacility = useCallback((updatedFacility) => {
    setFacility((prev) => ({ ...prev, ...updatedFacility }));
  }, []);

  const updatePersonalInfo = useCallback((newPersonalInfo) => {
    setPersonalInfo((prev) => ({ ...prev, ...newPersonalInfo }));
  }, []);

  const getScreenName = useCallback(() => {
    if (screenName) return screenName;
    if (personalInfo?.first_name) {
      return `${personalInfo.first_name} ${personalInfo.last_name || ""}`.trim();
    }
    if (facility?.name) return facility.name;
    if (user?.email) return user.email.split("@")[0];
    return "User";
  }, [screenName, personalInfo, facility, user]);

  const getRoleDisplayName = useCallback(() => {
    const currentRole = user?.user_type || role;
    const roleMap = {
      childcare_admin: "Admin",
      childcare_co_admin: "Co Admin",
      childcare_caregiver: "Caregiver",
      childcare_tutor: "Tutor",
    };
    return roleMap[currentRole] || "";
  }, [user?.user_type, role]);

  const getProfileImage = useCallback(() => {
    if (personalInfo?.profile_image) return personalInfo.profile_image;
    if (facility?.logo_url) return facility.logo_url;
    return null;
  }, [personalInfo?.profile_image, facility?.logo_url]);

  const getFacilityLogo = useCallback(
    () => facility?.logo_url || null,
    [facility?.logo_url],
  );

  const getFacilityName = useCallback(
    () => facility?.name || "",
    [facility?.name],
  );

  const getUserEmail = useCallback(() => user?.email || "", [user?.email]);

  const getUserInitials = useCallback(() => {
    const name = getScreenName();
    return name === "User" ? "U" : name.charAt(0).toUpperCase();
  }, [getScreenName]);

  const hasScreenName = useCallback(() => !!screenName, [screenName]);

  const isChildcareAdmin = useCallback(
    () => user?.user_type === "childcare_admin",
    [user?.user_type],
  );

  const isChildcareCoAdmin = useCallback(
    () => user?.user_type === "childcare_co_admin",
    [user?.user_type],
  );

  const isChildcareStaff = useCallback(
    () => user?.user_type === "childcare_caregiver" || user?.user_type === "childcare_tutor",
    [user?.user_type],
  );

  const hasChildcareAccess = useCallback(
    () => isChildcareAdmin() || isChildcareCoAdmin() || isChildcareStaff(),
    [isChildcareAdmin, isChildcareCoAdmin, isChildcareStaff],
  );

  return (
    <AuthContext.Provider
      value={{
        isLoggedIn,
        token,
        login,
        logout,
        currentUserId,
        user,
        userType,
        facilityId,
        loading,
        facility,
        personalInfo,
        screenName,
        updateScreenName,
        updateFacility,
        updateUser,
        updatePersonalInfo,
        refreshUserData,
        getScreenName,
        getRoleDisplayName,
        getProfileImage,
        getFacilityLogo,
        getFacilityName,
        getUserInitials,
        getUserEmail,
        hasScreenName,
        isChildcareAdmin,
        isChildcareCoAdmin,
        isChildcareStaff,
        hasChildcareAccess,
        approvalPending,
        clearApprovalPending,
        notifications,
        unreadCount,
        markAllRead,
        markOneRead,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// ✅ Custom hook for using AuthContext
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
